import {
	Box,
	Checkbox,
	IconButton,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TableSortLabel,
	Tooltip,
	Typography,
} from "@mui/material";
import { useState } from "react";
import { FileCopyOutlined as FileCopyOutlinedIcon, MoreVert as MoreVertIcon } from "@mui/icons-material";
import { LoadingComp } from "../../../shared/components/loading/Loading";
import { globalFormatCurrencyBRL, globalMaskDocument } from "../../../shared/utils";
import { formatDateFnsRemoveTimezone } from "../../../shared/utils/format-date";
import { IInstallmentTable } from "../types/types";
import { ModalType } from "../types/modalTexts";
import { useInstallmentHandlers } from "../hooks/useInstallmentHandlers";
import { useInstallmentModals } from "../hooks/useInstallmentModals";
import { GenericModalDetails } from "./Modals/GenericModalDetails";
import { ModalInstallmentLaunch } from "./Modals/ModalInstallmentLaunch";
import { ModalSelectInstallments } from "./Modals/ModalSelectInstallments";
import { InstallmentBilletMenu } from "./Menus/IntallmentBIlletCardMenu";
import { useExportInstallmentsExcel } from "../hooks/useExportInstallmentsExcel";
import { formatDateToBr, getDueDateFromDay } from "../utils/due_date_utils";
import { checkByProfileGuard } from "../../../shared/guards/ProfileGuard";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../shared/enums/profiles";
import { useProfile } from "../../../context/ProfileContext";

interface Props {
	rows: IInstallmentTable[];
	totalCount: number;
	page: number;
	rowsPerPage: number;
	onPageChange: (newPage: number) => void;
	onRowsPerPageChange: (value: number) => void;
	dataIni: string;
	dataFim: string;
	onRefresh: () => void;
	sortField: "name" | "value" | "count" | "due_date";
	sortOrder: "ASC" | "DESC";
	onSortChange: (field: "name" | "value" | "count" | "due_date") => void;
	selectedClientIds: string[];
	onToggleSelect: (clientId: string) => void;
	onToggleSelectAll: () => void;
}

export const TableInstallmentBillet = ({
	rows,
	totalCount,
	page,
	rowsPerPage,
	onPageChange,
	onRowsPerPageChange,
	dataIni,
	dataFim,
	onRefresh,
	sortField,
	sortOrder,
	onSortChange,
	onToggleSelect,
	onToggleSelectAll,
	selectedClientIds,
}: Props) => {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [hoveredName, setHoveredName] = useState<string | null>(null);
	const [hoveredDocument, setHoveredDocument] = useState<string | null>(null);
	const [selectModalType, setSelectModalType] = useState<ModalType | null>(null);
	const [isLaunchModalOpen, setIsLaunchModalOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const { modalType, selectedRow, openModal, closeModal } = useInstallmentModals();
	const { handleExportPdf } = useInstallmentHandlers(dataIni, dataFim, setIsLoading);
	const { handleExportCsv } = useExportInstallmentsExcel(dataIni, dataFim, "billet", setIsLoading);
	const { isSuperAdmin } = useProfile();
	const canLaunch = isSuperAdmin || checkByProfileGuard(PermissionRolesEnum.FinancialOptions, PermissionActionsEnum.Add);

	const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: IInstallmentTable) => {
		setAnchorEl(event.currentTarget);
		openModal(null, row);
	};

	const handleMenuClose = () => setAnchorEl(null);

	const handleCopy = (value: string) => navigator.clipboard.writeText(value).catch(console.error);

	const handleOpenModal = (type: ModalType | "details" | "generate" | "launch", row: IInstallmentTable) => {
		if (type === "details") {
			openModal("details", row);
		} else if (type === "launch") {
			if (!canLaunch) return;
			openModal(null, row);
			setIsLaunchModalOpen(true);
		} else {
			openModal(null, row);
			setSelectModalType(type);
		}
	};

	const handleExportToCsv = (row: IInstallmentTable) => {
		handleExportCsv(row.clientId, row.name);
	};

	const isSelected = (clientId: string) => selectedClientIds.includes(clientId);

	const sortableHeaders: Array<{
		label: string;
		field?: "name" | "value" | "count" | "due_date";
		align?: "left" | "center" | "right";
	}> = [
		{ label: "Data Criação" },
		{ label: "Parceiro", field: "name" },
		{ label: "Valor Total", field: "value" },
		{ label: "Garantias", field: "count", align: "center" },
		{ label: "Vencimento", field: "due_date", align: "center" },
		{ label: "Ações", align: "right" },
	];

	return (
		<>
			<TableContainer
				component={Paper}
				variant="elevation"
				sx={{ mt: 1, width: "96%", px: { xs: 1, sm: 3 }, py: { xs: 2, sm: 3 }, ml: 1, borderRadius: 2, overflow: "hidden" }}
			>
				<Table size="small" stickyHeader aria-label="Tabela de Faturamento">
					<TableHead>
						<TableRow sx={{ backgroundColor: "grey.100" }}>
							<TableCell padding="checkbox">
								<Checkbox
									checked={selectedClientIds.length === rows.length && rows.length > 0}
									indeterminate={selectedClientIds.length > 0 && selectedClientIds.length < rows.length}
									onChange={onToggleSelectAll}
								/>
							</TableCell>
							{sortableHeaders.map((header) => (
								<TableCell key={header.label} align={header.align ?? "left"}>
									{header.field ? (
										<TableSortLabel
											active={sortField === header.field}
											direction={sortField === header.field ? sortOrder.toLowerCase() as "asc" | "desc" : "asc"}
											onClick={() => onSortChange(header.field!)}
										>
											<Typography variant="button">{header.label}</Typography>
										</TableSortLabel>
									) : (
										<Typography variant="button">{header.label}</Typography>
									)}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.map((row) => {
							const selected = isSelected(row.clientId);
							return (
								<TableRow key={row.clientId} selected={selected}>
									<TableCell padding="checkbox">
										<Checkbox checked={selected} onChange={() => onToggleSelect(row.clientId)} />
									</TableCell>
									<TableCell>{formatDateFnsRemoveTimezone(row.data_generate)}</TableCell>
									<TableCell>
										<Stack spacing={0.5}>
											<Typography fontWeight="bold" noWrap>
												{row.name}
												{hoveredName === row.clientId && (
													<Tooltip title="Copiar Nome">
														<IconButton sx={{ fontSize: 10, ml: 2 }} onClick={() => handleCopy(row.name)}>
															<FileCopyOutlinedIcon sx={{ fontSize: 15 }} />
														</IconButton>
													</Tooltip>
												)}
											</Typography>
											<Typography
												variant="body2"
												color="textSecondary"
												noWrap
												onMouseEnter={() => setHoveredDocument(row.clientId)}
												onMouseLeave={() => setHoveredDocument(null)}
											>
												{globalMaskDocument(row.document)}
												{hoveredDocument === row.clientId && (
													<Tooltip title="Copiar Documento">
														<IconButton sx={{ fontSize: 10, ml: 1 }} onClick={() => handleCopy(row.document)}>
															<FileCopyOutlinedIcon sx={{ fontSize: 15 }} />
														</IconButton>
													</Tooltip>
												)}
											</Typography>
										</Stack>
									</TableCell>
									<TableCell>
										<Typography fontWeight={500} color="textSecondary">
											{globalFormatCurrencyBRL(row.sum_amount)}
										</Typography>
									</TableCell>
									<TableCell align="center">
										<Typography variant="body2" color="textSecondary">
											{row.count_amount}
										</Typography>
									</TableCell>
									<TableCell align="center">
										<Typography variant="body2">{formatDateToBr(getDueDateFromDay(Number(row.day_due_date)))}</Typography>
									</TableCell>
									<TableCell align="right">
										<IconButton onClick={(e) => handleMenuOpen(e, row)} aria-label={`Ações para ${row.name}`}>
											<MoreVertIcon />
										</IconButton>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>

				<InstallmentBilletMenu
					anchorEl={anchorEl}
					open={Boolean(anchorEl)}
					onClose={handleMenuClose}
					selectedRow={selectedRow}
					onOpenModal={handleOpenModal}
					onExportPdf={handleExportPdf}
					onExportCsv={handleExportToCsv}
				/>

				<Box display="flex" justifyContent="flex-end" alignItems="center" mt={2} pr={2}>
					<TablePagination
						component="div"
						count={totalCount}
						page={page}
						rowsPerPage={rowsPerPage}
						onPageChange={(_, newPage) => onPageChange(newPage)}
						onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
						rowsPerPageOptions={[10, 25, 50]}
						labelRowsPerPage="Linhas por página:"
						labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} registros`}
					/>
				</Box>
			</TableContainer>

			<LoadingComp isopen={isLoading} />

			{modalType === "details" && selectedRow && (
				<GenericModalDetails open onClose={closeModal} dataIni={dataIni} dataFim={dataFim} row={selectedRow} paymentMethod="billet" />
			)}

			{selectModalType && selectedRow && (
				<ModalSelectInstallments
					open
					onClose={() => setSelectModalType(null)}
					dataIni={dataIni}
					dataFim={dataFim}
					row={selectedRow}
					modalType={selectModalType}
					filterByPaymentMethod="billet"
					onSuccess={onRefresh}
				/>
			)}

			{isLaunchModalOpen && selectedRow && (
				<ModalInstallmentLaunch
					open
					onClose={() => setIsLaunchModalOpen(false)}
					row={selectedRow}
					dataIni={dataIni}
					dataFim={dataFim}
					onSuccess={onRefresh}
				/>
			)}
		</>
	);
};
